import React, { useState } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Input,
  Form,
  Tag,
  Alert,
  Select,
  message,
  Spin,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { supabase } from "../../../lib/supabase";
import { trackFeatureUsage } from "../../../lib/posthog";
import {
  getCompanyUsers,
  getUserCompanies,
  getUsers,
} from "../../../services/adminUsers";
import {
  BOOKING_INSIGHT_SELECT,
  BOOKING_LIST_SELECT,
  OPEN_BOOKING_STATUSES,
  buildBookingLookupOrFilter,
  buildBotBookingPayload,
  extractBookingTokenFromMessage,
  formatBookingEffLabel,
  normalizeEmail,
  normalizePhone,
  phonesLikelyMatch,
  pickBestBookingMatch,
  type BookingInsight,
  type BookingListRow,
  type CompanyMatchRow,
  type MatchReason,
} from "./botAdminShared";
import { enrichBotBooking } from "./enrichBotBooking";

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

type ChatRole = "customer" | "bot" | "system";

interface ChatBubble {
  id: string;
  role: ChatRole;
  text: string;
  step?: string;
}

function bubbleId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function resolveCompaniesFromIdentity(opts: {
  email: string;
  phone: string;
}): Promise<{
  companies: CompanyMatchRow[];
  matchedUserEmails: string[];
}> {
  const { email, phone } = opts;
  const companyById = new Map<number, CompanyMatchRow>();

  const addCompany = (
    row: {
      id: number;
      name: string;
      status?: string | null;
      phone_number?: string | null;
      invoice_email?: string | null;
      city?: string | null;
      country?: string | null;
    },
    reason: MatchReason,
  ) => {
    const existing = companyById.get(row.id);
    if (existing) {
      if (!existing.matchReasons.includes(reason)) {
        existing.matchReasons.push(reason);
      }
      return;
    }
    companyById.set(row.id, {
      id: row.id,
      name: row.name,
      status: row.status,
      phone_number: row.phone_number,
      invoice_email: row.invoice_email,
      city: row.city,
      country: row.country,
      matchReasons: [reason],
    });
  };

  if (email || phone) {
    const orParts: string[] = [];
    if (email) orParts.push(`invoice_email.ilike.%${email}%`);
    if (phone) {
      const digits = normalizePhone(phone);
      orParts.push(`phone_number.ilike.%${phone}%`);
      if (digits && digits !== phone) {
        orParts.push(`phone_number.ilike.%${digits}%`);
      }
    }

    const { data: companies, error } = await supabase
      .from("company")
      .select("id, name, status, phone_number, invoice_email, city, country")
      .or(orParts.join(","))
      .limit(50);
    if (error) throw error;

    for (const row of companies ?? []) {
      const emailHit =
        !!email &&
        !!row.invoice_email &&
        normalizeEmail(row.invoice_email).includes(email);
      const phoneHit = !!phone && phonesLikelyMatch(row.phone_number, phone);
      if (emailHit) addCompany(row, "company_invoice_email");
      if (phoneHit) addCompany(row, "company_phone");
      if (!emailHit && !phoneHit) {
        if (email) addCompany(row, "company_invoice_email");
        else addCompany(row, "company_phone");
      }
    }
  }

  const matchedUserEmails: string[] = [];
  if (email) {
    const { data: usersData, error: usersError } = await getUsers(
      1,
      50,
      email,
      "email",
      "ascend",
    );
    if (usersError) throw usersError;
    const users =
      (usersData as { users?: Array<{ id: string; email?: string }> })?.users ??
      [];

    for (const user of users) {
      if (!user.email) continue;
      if (!normalizeEmail(user.email).includes(email)) continue;
      matchedUserEmails.push(user.email);

      const { data: ucData, error: ucError } = await getUserCompanies(user.id);
      if (ucError) throw ucError;
      const links =
        (ucData as {
          user_companies?: Array<{
            company_id: number;
            company?: {
              id: number;
              name: string;
              status?: string | null;
              city?: string | null;
              country?: string | null;
            } | null;
          }>;
        })?.user_companies ?? [];

      for (const link of links) {
        if (link.company) {
          addCompany(
            {
              id: link.company.id,
              name: link.company.name,
              status: link.company.status,
              city: link.company.city,
              country: link.company.country,
              phone_number: null,
              invoice_email: null,
            },
            "user_email",
          );
        }
      }
    }
  }

  // Enrich missing contact fields
  for (const [id, row] of companyById) {
    if (row.phone_number != null && row.invoice_email != null) continue;
    const { data: full } = await supabase
      .from("company")
      .select("id, name, status, phone_number, invoice_email, city, country")
      .eq("id", id)
      .maybeSingle();
    if (full) {
      companyById.set(id, {
        ...row,
        ...full,
        matchReasons: row.matchReasons,
      });
    }
  }

  return {
    companies: [...companyById.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    ),
    matchedUserEmails,
  };
}

async function lookupBookingForCompany(
  companyId: number,
  token: string,
): Promise<BookingInsight | null> {
  const orFilter = buildBookingLookupOrFilter(token);
  const { data, error } = await supabase
    .from("bookings")
    .select(BOOKING_INSIGHT_SELECT)
    .eq("company_id", companyId)
    .or(orFilter)
    .limit(20);
  if (error) throw error;
  const rows = (data ?? []) as unknown as BookingInsight[];
  const best = pickBestBookingMatch(rows, token);
  if (!best) return null;
  return enrichBotBooking(best);
}

async function loadOpenBookingsForCompany(
  companyId: number,
): Promise<BookingListRow[]> {
  const { data, error } = await supabase
    .from("bookings")
    .select(BOOKING_LIST_SELECT)
    .eq("company_id", companyId)
    .in("status", [...OPEN_BOOKING_STATUSES])
    .order("created_at", { ascending: false })
    .limit(15);
  if (error) throw error;
  return (data ?? []) as unknown as BookingListRow[];
}

function formatOpenBookingsList(rows: BookingListRow[]): string {
  return rows
    .map((row, i) => {
      const ref = formatBookingEffLabel(row);
      const eta =
        row.eta ||
        row.container?.eta_customer ||
        row.container?.eta ||
        null;
      const etaBit = eta ? ` · ETA ${String(eta).slice(0, 10)}` : "";
      return `${i + 1}. ${ref} — ${row.status ?? "—"}${etaBit}`;
    })
    .join("\n");
}

export const BotSampleConversation: React.FC = () => {
  const [form] = Form.useForm<{
    channel: "whatsapp" | "email";
    email?: string;
    phone?: string;
    message: string;
  }>();
  const [running, setRunning] = useState(false);
  const [bubbles, setBubbles] = useState<ChatBubble[]>([]);

  const push = (role: ChatRole, text: string, step?: string) => {
    setBubbles((prev) => [...prev, { id: bubbleId(), role, text, step }]);
  };

  const handleClear = () => setBubbles([]);

  const handleRun = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    const email = normalizeEmail(values.email ?? "");
    const phone = (values.phone ?? "").trim();
    const customerMessage = values.message.trim();

    if (!email && !phone) {
      message.warning("Enter an email and/or phone (channel identity).");
      return;
    }
    if (!customerMessage) {
      message.warning("Enter a customer message.");
      return;
    }

    setRunning(true);
    setBubbles([]);
    trackFeatureUsage("admin_bot_sample_conversation");

    try {
      const channelLabel =
        values.channel === "whatsapp" ? "WhatsApp" : "Gmail";
      const identityLabel = phone || email;

      push(
        "system",
        `Incoming ${channelLabel} from ${identityLabel}`,
        "channel",
      );
      push("customer", customerMessage);

      // Step 1 — resolve
      push("system", "Step 1 — Resolve identity…", "1.resolve");
      const { companies, matchedUserEmails } =
        await resolveCompaniesFromIdentity({ email, phone });

      if (companies.length === 0) {
        push(
          "bot",
          "Sorry, I couldn’t find an eFreight account for this contact. Please make sure you’re messaging from a registered email/phone, or contact support.",
        );
        return;
      }

      push(
        "system",
        `Matched ${companies.length} compan${
          companies.length === 1 ? "y" : "ies"
        }${
          matchedUserEmails.length
            ? ` via users: ${matchedUserEmails.join(", ")}`
            : ""
        }: ${companies.map((c) => `${c.name} (#${c.id})`).join(", ")}`,
        "1.resolve",
      );

      // Step 2 — pick company
      let company = companies[0];
      if (companies.length > 1) {
        push(
          "bot",
          `I found multiple companies linked to you:\n${companies
            .map((c, i) => `${i + 1}. ${c.name}`)
            .join(
              "\n",
            )}\n\nFor this sample I’ll continue with the first one: ${company.name}.`,
          "2.company",
        );
      } else {
        push(
          "system",
          `Step 2 — Company scoped to ${company.name} (#${company.id})`,
          "2.company",
        );
      }

      // Load users for company context (parity with step 2)
      const { data: cuData } = await getCompanyUsers(company.id);
      const users =
        (cuData as { users?: Array<{ id: string; email?: string }> })?.users ??
        [];

      // Step 3 — booking from message, or suggest open bookings
      const token = extractBookingTokenFromMessage(customerMessage);
      push(
        "system",
        token
          ? `Step 3 — Extracted booking token “${token}”, looking up…`
          : "Step 3 — No booking id/reference in message → load recent open bookings",
        "3.insights",
      );

      const companyPayload = {
        id: company.id,
        name: company.name,
        status: company.status ?? null,
        phone_number: company.phone_number ?? null,
        invoice_email: company.invoice_email ?? null,
        city: company.city ?? null,
        country: company.country ?? null,
        users: users.map((u) => ({
          id: u.id,
          email: u.email ?? "(no email)",
        })),
      };

      if (!token) {
        const openBookings = await loadOpenBookingsForCompany(company.id);
        if (openBookings.length === 0) {
          push(
            "bot",
            `I couldn’t find any open bookings for ${company.name}. If you have a specific reference (e.g. EFF7668SEA), send it and I’ll look it up.`,
          );
          return;
        }

        const listText = formatOpenBookingsList(openBookings);
        push(
          "system",
          `Found ${openBookings.length} open booking(s) for company #${company.id}`,
          "3.insights",
        );

        push(
          "system",
          "Step 4 — Asking OpenAI to present the list and ask which booking…",
          "4.reply",
        );

        const { data, error } = await supabase.functions.invoke("bot-reply", {
          body: {
            question: customerMessage,
            company: companyPayload,
            booking: null,
            open_bookings: openBookings.map((row) => ({
              id: row.id,
              eff_reference: formatBookingEffLabel(row),
              reference: row.reference,
              status: row.status,
              eta:
                row.eta ||
                row.container?.eta_customer ||
                row.container?.eta ||
                null,
              container_reference: row.container?.reference ?? null,
            })),
            intent: "suggest_open_bookings",
          },
        });
        if (error) throw error;

        const payload = data as {
          answer?: string;
          error?: string;
          today?: { today_ymd?: string; today_weekday?: string };
        } | null;

        if (payload?.error) throw new Error(payload.error);

        // Fallback if OpenAI fails or returns empty: still show the list in chat
        const fallback = `Ik zie deze openstaande boekingen voor ${company.name}:\n${listText}\n\nWelke bedoel je? Antwoord met het referentienummer (bijv. EFF7668SEA).`;
        push("bot", payload?.answer?.trim() || fallback, "4.reply");
        return;
      }

      const booking = await lookupBookingForCompany(company.id, token);
      if (!booking) {
        // Token given but not found — still offer open bookings as alternatives
        const openBookings = await loadOpenBookingsForCompany(company.id);
        if (openBookings.length > 0) {
          const listText = formatOpenBookingsList(openBookings);
          push(
            "bot",
            `I couldn’t find booking ${token} under ${company.name}.\n\nHere are your open bookings:\n${listText}\n\nReply with the correct reference.`,
          );
        } else {
          push(
            "bot",
            `I couldn’t find booking ${token} under ${company.name}. Double-check the reference, or ask about another booking.`,
          );
        }
        return;
      }

      const eff = formatBookingEffLabel({
        id: booking.id,
        reference: booking.reference,
        transport_type: booking.transport_type,
      });
      push(
        "system",
        `Loaded ${eff} · status ${booking.status ?? "—"} (full JSON + LeanCustoms/timeline attached for OpenAI)`,
        "3.insights",
      );

      // Step 4 — OpenAI reply
      push("system", "Step 4 — Asking OpenAI with company + booking context…", "4.reply");

      const bookingPayload = buildBotBookingPayload(booking, company.id);

      const { data, error } = await supabase.functions.invoke("bot-reply", {
        body: {
          question: customerMessage,
          company: companyPayload,
          booking: bookingPayload,
        },
      });
      if (error) throw error;

      const payload = data as {
        answer?: string;
        error?: string;
        today?: { today_ymd?: string; today_weekday?: string };
      } | null;

      if (payload?.error) throw new Error(payload.error);
      if (!payload?.answer) throw new Error("No answer from bot-reply");

      if (payload.today?.today_ymd) {
        push(
          "system",
          `Bot “today”: ${payload.today.today_weekday ?? ""} ${payload.today.today_ymd}`.trim(),
          "4.reply",
        );
      }

      push("bot", payload.answer, "4.reply");
    } catch (err) {
      console.error("Sample conversation failed:", err);
      message.error(
        err instanceof Error ? err.message : "Sample conversation failed",
      );
      push(
        "system",
        `Error: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <MessageOutlined />
          <span>Full sample conversation</span>
          <Tag color="purple">End-to-end</Tag>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Paragraph type="secondary">
        Simulates a real WhatsApp/Gmail turn: customer message → resolve
        identity → company → booking lookup → OpenAI reply. Uses live data and
        the same pipeline as the step-by-step builder.
      </Paragraph>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          channel: "whatsapp",
          email: "",
          phone: "",
          message: "Hi, waar is EFF7668SEA?",
        }}
        style={{ maxWidth: 640, marginBottom: 16 }}
      >
        <Form.Item name="channel" label="Channel">
          <Select
            options={[
              { value: "whatsapp", label: "WhatsApp" },
              { value: "email", label: "Gmail" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="email"
          label="Customer email"
          rules={[{ type: "email", message: "Valid email" }]}
        >
          <Input placeholder="user@company.com" allowClear />
        </Form.Item>
        <Form.Item name="phone" label="Customer phone">
          <Input placeholder="+31 6 …" allowClear />
        </Form.Item>
        <Form.Item
          name="message"
          label="Customer message"
          rules={[{ required: true, message: "Enter a message" }]}
        >
          <TextArea
            rows={3}
            placeholder="e.g. Hi, waar is EFF7668SEA?"
            maxLength={2000}
            showCount
          />
        </Form.Item>
        <Space>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={running}
            onClick={handleRun}
          >
            Run sample
          </Button>
          <Button
            icon={<ClearOutlined />}
            onClick={handleClear}
            disabled={running || bubbles.length === 0}
          >
            Clear
          </Button>
        </Space>
      </Form>

      {running && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin tip="Walking through all steps…" />
        </div>
      )}

      {bubbles.length > 0 && (
        <div
          style={{
            background: "#f5f5f5",
            borderRadius: 8,
            padding: 16,
            maxHeight: 640,
            overflow: "auto",
          }}
        >
          <Title level={5} style={{ marginTop: 0 }}>
            Transcript
          </Title>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {bubbles.map((b) => {
              const isCustomer = b.role === "customer";
              const isBot = b.role === "bot";
              return (
                <div
                  key={b.id}
                  style={{
                    display: "flex",
                    justifyContent: isCustomer ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "85%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: isCustomer
                        ? "#d9f7be"
                        : isBot
                          ? "#fff"
                          : "#e6f4ff",
                      border: isBot ? "1px solid #f0f0f0" : undefined,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <Space size={6} style={{ marginBottom: 4 }}>
                      {isCustomer ? (
                        <UserOutlined />
                      ) : isBot ? (
                        <RobotOutlined />
                      ) : (
                        <Tag style={{ margin: 0 }}>pipeline</Tag>
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {isCustomer
                          ? "Customer"
                          : isBot
                            ? "Bot"
                            : "Behind the scenes"}
                      </Text>
                      {b.step && <Tag>{b.step}</Tag>}
                    </Space>
                    <div>{b.text}</div>
                  </div>
                </div>
              );
            })}
          </Space>
        </div>
      )}

      {!running && bubbles.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="No transcript yet"
          description="Fill identity + a message that includes a booking reference, then Run sample."
        />
      )}
    </Card>
  );
};
